/**
 * Job Type Definition
 * This type is used consistently across the application (calendar, profile, history, etc.)
 */

/**
 * @typedef {Object} Job
 * @property {number} id - Unique job identifier
 * @property {number} employer_id - ID of the employer who posted the job
 * @property {string} employer_name - Name of the employer/practice
 * @property {string} job_role - Job role (Nurse, Dentist, Hygienist, Therapist, Receptionist)
 * @property {string} job_date - Job date in YYYY-MM-DD format
 * @property {string} start_time - Start time in HH:MM format
 * @property {string} end_time - End time in HH:MM format
 * @property {number} total_hours - Total hours for the job
 * @property {string} status - Job status: 'posted' | 'matched' | 'accepted' | 'in_progress' | 'completed' | 'cancelled' | 'pending_payment'
 * @property {string} practice_address - Address of the practice
 * @property {string} created_at - ISO timestamp when job was created
 * @property {string} [updated_at] - ISO timestamp when job was last updated
 */

/**
 * @typedef {Object} Match
 * @property {number} id - Unique match identifier
 * @property {number} job_id - ID of the matched job
 * @property {number} employee_id - ID of the matched employee
 * @property {string} [employee_name] - Name of the employee
 * @property {number} distance_miles - Distance in miles between employee and practice
 * @property {string} employee_status - 'pending' | 'accepted' | 'rejected'
 * @property {string} employer_status - 'pending' | 'accepted' | 'rejected'
 * @property {string} matched_at - ISO timestamp when match was created
 * @property {string} [employee_responded_at] - ISO timestamp when employee responded
 * @property {string} [employer_responded_at] - ISO timestamp when employer responded
 * @property {Job} job - The matched job object
 */

/**
 * @typedef {Object} JobWithMatch
 * @property {Job} job - The job object
 * @property {Match} [match] - The match object if applicable
 */

// Export types for use in other files
export const JOB_STATUS = {
  POSTED: 'posted',
  MATCHED: 'matched',
  ACCEPTED: 'accepted',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  PENDING_PAYMENT: 'pending_payment'
};

export const MATCH_STATUS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected'
};

// Helper function to format job for display
export const formatJobTitle = (job, userRole, match = null) => {
  if (!job) return 'Job Details';
  
  // Get job role - handle nested structures
  // If job_role is a number (ID), get the name from job_role_detail
  let jobRole;
  if (typeof job.job_role === 'number') {
    // job_role is an ID, get name from job_role_detail
    jobRole = job.job_role_detail?.job_role_name 
      || job.job_role_detail?.job_role
      || job.job_role_name
      || 'N/A';
  } else if (job.job_role && typeof job.job_role === 'string') {
    // job_role is already a string (name)
    jobRole = job.job_role;
  } else {
    // Fallback to job_role_detail or other sources
    jobRole = job.job_role_detail?.job_role_name 
      || job.job_role_detail?.job_role
      || job.job_role_name
      || 'N/A';
  }
  
  // Get employer name - handle nested structures
  let employerName = job.employer_name;
  if (!employerName) {
    if (job.employer?.first_name && job.employer?.last_name) {
      employerName = `${job.employer.first_name} ${job.employer.last_name}`;
    } else if (job.employer?.practice_name) {
      employerName = job.employer.practice_name;
    } else if (job.employer?.email) {
      employerName = job.employer.email;
    } else {
      employerName = 'N/A';
    }
  }
  
  if (userRole === 'employee') {
    return `${jobRole} - ${employerName}`;
  } else if (userRole === 'employer') {
    // For employers, show just the job role name (no employee name for posted jobs)
    // Only show employee name if there's a match with employee details
    if (match && (match.employee_detail || match.employee_name)) {
      let employeeName = 'Employee';
      if (match.employee_detail?.first_name && match.employee_detail?.last_name) {
        employeeName = `${match.employee_detail.first_name} ${match.employee_detail.last_name}`;
      } else if (match.employee_name) {
        employeeName = match.employee_name;
      }
      return `${jobRole} - ${employeeName}`;
    }
    // For posted jobs without matches, just show the job role
    return jobRole;
  }
  return jobRole;
};

// Helper function to get job status label
export const getJobStatusLabel = (status) => {
  const labels = {
    // Frontend statuses
    [JOB_STATUS.POSTED]: 'Posted',
    [JOB_STATUS.MATCHED]: 'Matched',
    [JOB_STATUS.ACCEPTED]: 'Accepted',
    [JOB_STATUS.IN_PROGRESS]: 'In Progress',
    [JOB_STATUS.COMPLETED]: 'Completed',
    [JOB_STATUS.CANCELLED]: 'Cancelled',
    [JOB_STATUS.PENDING_PAYMENT]: 'Pending Payment',
    // Backend statuses (job-postings API)
    'open': 'Open',
    'match_found': 'Match Found',
    'accepted': 'Accepted',
    'done': 'Done'
  };
  return labels[status] || status;
};

// Helper function to get job status color
export const getJobStatusColor = (status) => {
  const colors = {
    // Frontend statuses
    [JOB_STATUS.POSTED]: '#ff6347',
    [JOB_STATUS.MATCHED]: '#ffa500',
    [JOB_STATUS.ACCEPTED]: '#4169e1',
    [JOB_STATUS.IN_PROGRESS]: '#9370db',
    [JOB_STATUS.COMPLETED]: '#9370db',
    [JOB_STATUS.CANCELLED]: '#dc143c',
    [JOB_STATUS.PENDING_PAYMENT]: '#ffa500',
    // Backend statuses (job-postings API)
    'open': '#ff6347',
    'match_found': '#ffa500',
    'accepted': '#4169e1',
    'done': '#9370db'
  };
  return colors[status] || '#6b7280';
};









